/**
 * wordWrap.ts — Word-wrapping utility for narrative text.
 *
 * Provides pure functions for wrapping text to a maximum display width,
 * respecting {color:text} markup tags used throughout RotaryKnights.
 *
 * The color markup format is {color:text} where color is a ColorName
 * (e.g., cyan, green, red, yellow). These tags affect rendering but
 * should not count toward display width.
 */

/** Regex matching a complete {color:text} markup tag (non-greedy inner). */
const MARKUP_TAG_RE = /\{([a-zA-Z]+):([^}]*)\}/g;

/**
 * Calculate the visual display length of a string,
 * excluding {color:text} markup tags.
 *
 * e.g. "{cyan:hello} world" → 11 ("hello world")
 *      "plain text" → 10
 *      "{red:a}{green:b}" → 2
 */
export function visualLength(text: string): number {
    const stripped = text.replace(MARKUP_TAG_RE, (_match, _color: string, inner: string) => inner);
    return stripped.length;
}

/**
 * Segment types produced by the markup-aware parser.
 * - 'plain': plain text (no markup wrapper)
 * - 'markup': a complete {color:text} tag
 */
interface Segment {
    type: 'plain' | 'markup';
    raw: string;
    /** The visible text (inner text for markup, raw for plain) */
    display: string;
    /** Color name, only for markup segments */
    color?: string;
}

/**
 * Parse text into segments, separating markup tags from plain text.
 * This preserves the exact raw text for reconstruction.
 */
function parseSegments(text: string): Segment[] {
    const segments: Segment[] = [];
    let remaining = text;

    while (remaining.length > 0) {
        const braceIdx = remaining.indexOf('{');

        if (braceIdx === -1) {
            // No more braces — rest is plain text
            segments.push({ type: 'plain', raw: remaining, display: remaining });
            break;
        }

        // Push plain text before the brace
        if (braceIdx > 0) {
            const plain = remaining.slice(0, braceIdx);
            segments.push({ type: 'plain', raw: plain, display: plain });
        }

        const closeIdx = remaining.indexOf('}', braceIdx);
        if (closeIdx === -1) {
            // Unclosed brace — treat rest as plain text (malformed markup)
            const rest = remaining.slice(braceIdx);
            segments.push({ type: 'plain', raw: rest, display: rest });
            break;
        }

        const inner = remaining.slice(braceIdx + 1, closeIdx);
        const colonIdx = inner.indexOf(':');

        if (colonIdx === -1 || !/^[a-zA-Z]+$/.test(inner.slice(0, colonIdx))) {
            // Not valid markup — treat as plain text
            const raw = remaining.slice(braceIdx, closeIdx + 1);
            segments.push({ type: 'plain', raw, display: raw });
        } else {
            const color = inner.slice(0, colonIdx);
            const innerText = inner.slice(colonIdx + 1);
            const raw = remaining.slice(braceIdx, closeIdx + 1);
            segments.push({ type: 'markup', raw, display: innerText, color });
        }

        remaining = remaining.slice(closeIdx + 1);
    }

    return segments;
}

/**
 * A "word" in the wrapping algorithm. May consist of multiple segments
 * (e.g., plain text + markup + plain text all without whitespace between them).
 */
interface Word {
    /** The segments that make up this word */
    segments: Segment[];
    /** The full raw text of the word */
    raw: string;
    /** The visual display length */
    visualLen: number;
}

/**
 * Build words from segments by splitting on whitespace boundaries.
 * Whitespace within markup tags creates separate words but preserves
 * the color context.
 *
 * Returns an array of Word objects and space strings interleaved.
 */
type WordOrSpace = { type: 'word'; word: Word } | { type: 'space'; raw: string; visualLen: number };

function buildWords(segments: Segment[]): WordOrSpace[] {
    const result: WordOrSpace[] = [];

    // Flatten segments into a stream of characters with color context,
    // then re-split on whitespace boundaries.
    // We need to track which color (if any) each character belongs to.

    interface CharInfo {
        char: string;
        color: string | null;
    }

    const chars: CharInfo[] = [];
    for (const seg of segments) {
        if (seg.type === 'markup') {
            for (const ch of seg.display) {
                chars.push({ char: ch, color: seg.color ?? null });
            }
        } else {
            for (const ch of seg.display) {
                chars.push({ char: ch, color: null });
            }
        }
    }

    // Now group chars into words (non-space runs) and spaces
    let i = 0;
    while (i < chars.length) {
        if (chars[i].char === ' ' || chars[i].char === '\t') {
            // Collect space run
            let spaceRaw = '';
            while (i < chars.length && (chars[i].char === ' ' || chars[i].char === '\t')) {
                spaceRaw += chars[i].char;
                i++;
            }
            result.push({ type: 'space', raw: spaceRaw, visualLen: spaceRaw.length });
        } else {
            // Collect word run — group consecutive non-space chars, wrapping
            // colored chars in markup tags
            const wordChars: CharInfo[] = [];
            while (i < chars.length && chars[i].char !== ' ' && chars[i].char !== '\t') {
                wordChars.push(chars[i]);
                i++;
            }

            // Reconstruct the raw text with markup tags
            let raw = '';
            let currentColor: string | null = null;
            for (const wc of wordChars) {
                if (wc.color !== currentColor) {
                    if (currentColor !== null) {
                        raw += '}';
                    }
                    if (wc.color !== null) {
                        raw += `{${wc.color}:`;
                    }
                    currentColor = wc.color;
                }
                raw += wc.char;
            }
            if (currentColor !== null) {
                raw += '}';
            }

            const display = wordChars.map(wc => wc.char).join('');
            result.push({
                type: 'word',
                word: {
                    segments: [], // Not needed for wrapping
                    raw,
                    visualLen: display.length,
                },
            });
        }
    }

    return result;
}

/**
 * Word-wrap a string to fit within maxWidth display characters.
 * Respects {color:text} markup — measures visual length only,
 * never breaks inside a markup tag.
 *
 * Returns an array of lines, each with visualLength <= maxWidth.
 * Blank input returns [''].
 *
 * When a color markup tag spans a line break, the tag is closed at the
 * end of the current line and re-opened on the continuation line.
 *
 * @param text - The input string to wrap
 * @param maxWidth - Maximum visual width per line
 * @returns Array of wrapped lines
 */
export function wordWrap(text: string, maxWidth: number): string[] {
    // Edge case: empty input
    if (text === '') return [''];

    // Edge case: maxWidth <= 0 — no wrapping
    if (maxWidth <= 0) return [text];

    const segments = parseSegments(text);
    const wordsAndSpaces = buildWords(segments);

    if (wordsAndSpaces.length === 0) return [''];

    const lines: string[] = [];
    let currentLine = '';
    let visualCol = 0;

    for (const item of wordsAndSpaces) {
        if (item.type === 'space') {
            // Only add space if we're not at the start of a line
            if (currentLine !== '') {
                if (visualCol + item.visualLen <= maxWidth) {
                    currentLine += item.raw;
                    visualCol += item.visualLen;
                }
                // If space would exceed width, skip it (natural break point)
            }
            continue;
        }

        // It's a word
        const word = item.word;

        if (visualCol === 0) {
            // First word on the line — always place it (even if oversized)
            currentLine = word.raw;
            visualCol = word.visualLen;
        } else if (visualCol + word.visualLen <= maxWidth) {
            // Word fits on current line (space was already added)
            currentLine += word.raw;
            visualCol += word.visualLen;
        } else {
            // Word doesn't fit — emit current line and start new one
            lines.push(currentLine.replace(/\s+$/, ''));
            currentLine = word.raw;
            visualCol = word.visualLen;
        }
    }

    // Emit the last line
    if (currentLine !== '' || lines.length === 0) {
        lines.push(currentLine.replace(/\s+$/, ''));
    }

    return lines;
}

/**
 * Join consecutive non-blank lines into single paragraph strings,
 * preserving blank lines as paragraph separators.
 *
 * This is used to convert pre-wrapped narrative text (manually broken
 * at ~55 chars) into unwrapped paragraphs that can be dynamically
 * word-wrapped at render time to any panel width.
 *
 * Color_Markup tags ({color:text}) are preserved intact during joining.
 *
 * Example:
 *   Input:  ['July 1914. After months of letters', 'and interviews, you have', '', 'The {cyan:RFC} is barely two years old.']
 *   Output: ['July 1914. After months of letters and interviews, you have', '', 'The {cyan:RFC} is barely two years old.']
 *
 * @param lines - Array of pre-wrapped narrative lines, with blank strings as paragraph separators
 * @returns Array of unwrapped paragraph strings with blank-line separators preserved
 */
export function joinParagraphs(lines: string[]): string[] {
    const result: string[] = [];
    let currentParagraph = '';

    for (const line of lines) {
        if (line === '') {
            // Blank line = paragraph separator
            // Flush any accumulated paragraph text first
            if (currentParagraph !== '') {
                result.push(currentParagraph);
                currentParagraph = '';
            }
            result.push('');
        } else {
            // Non-blank line — append to current paragraph
            if (currentParagraph === '') {
                currentParagraph = line;
            } else {
                currentParagraph += ' ' + line;
            }
        }
    }

    // Flush any remaining paragraph text
    if (currentParagraph !== '') {
        result.push(currentParagraph);
    }

    return result;
}
